import React, { useState, useEffect } from 'react';
import api from '../services/api';

export default function WaitlistPage() {
  const [form, setForm] = useState({ fullName: '', email: '', type: 'CLIENT' });
  const [status, setStatus] = useState(null); // null | 'success' | 'already' | 'error'
  const [position, setPosition] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.fullName) return;
    setSubmitting(true);
    try {
      const { data } = await api.post('/waitlist', form);
      setStatus('success');
      setPosition(data.position);
    } catch (err) {
      if (err.response?.status === 409) setStatus('already');
      else setStatus('error');
    }
    setSubmitting(false);
  };

  return (
    <div className="fixed inset-0 bg-[#0a0a0f] flex flex-col items-center justify-between px-6 overflow-y-auto overflow-x-hidden">
      {/* Background glow */}
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 rounded-full opacity-15 blur-[100px]"
        style={{ background: 'radial-gradient(circle, #6C5CE7, #a855f7, transparent)' }} />

      <div className="relative z-10 w-full max-w-md flex flex-col items-center text-center" style={{ paddingTop: 'calc(env(safe-area-inset-top, 20px) + 24px)' }}>
        {/* Logo */}
        <img src="/logo-dark.png" alt="findr" style={{ height: 80, width: 'auto', objectFit: 'contain' }} className="mb-4" />

        <p className="text-gray-400 text-sm mb-2">THE GCC'S CREATIVE MARKETPLACE</p>

        <h1 className="text-white text-3xl font-bold mb-3" style={{ letterSpacing: '-0.5px' }}>
          Something big is coming.
        </h1>
        <p className="text-gray-400 text-base mb-8 max-w-sm">
          Hire top creative talent, book studios, and get things done — all in one place. Be the first to know when we launch.
        </p>

        {/* Feature pills */}
        <div className="flex flex-wrap justify-center gap-2 mb-8">
          {['Design', 'Video', 'Marketing', 'Studios', 'Web Dev', 'Content'].map(f => (
            <span key={f} className="text-xs px-3 py-1.5 rounded-full border border-gray-700/50 text-gray-400 bg-white/5">
              {f}
            </span>
          ))}
        </div>

        {status === 'success' ? (
          <div className="w-full bg-white/5 border border-gray-700/50 rounded-2xl p-6 backdrop-blur">
            <div className="text-4xl mb-3">🎉</div>
            <h2 className="text-white text-xl font-bold mb-2">You're in!</h2>
            <p className="text-gray-400 text-sm mb-4">
              You're <span className="text-purple-400 font-bold">#{position}</span> on the waitlist.
              We'll email you when it's your turn.
            </p>
            <div className="bg-white/5 rounded-xl px-4 py-3">
              <p className="text-gray-500 text-xs">People on the waitlist</p>
              <p className="text-white text-2xl font-bold">117</p>
            </div>
          </div>
        ) : status === 'already' ? (
          <div className="w-full bg-white/5 border border-gray-700/50 rounded-2xl p-6 backdrop-blur">
            <div className="text-4xl mb-3">👋</div>
            <h2 className="text-white text-xl font-bold mb-2">You're already on the list!</h2>
            <p className="text-gray-400 text-sm">We'll notify you when Findr launches. Stay tuned.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="w-full flex flex-col gap-3">
            <input
              type="text"
              placeholder="Your full name"
              value={form.fullName}
              onChange={e => setForm(f => ({ ...f, fullName: e.target.value }))}
              className="w-full px-4 py-3.5 rounded-xl bg-white/5 border border-gray-700/50 text-white placeholder-gray-500 text-sm focus:outline-none focus:border-purple-500/50"
              required
            />
            <input
              type="email"
              placeholder="Your email address"
              value={form.email}
              onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
              className="w-full px-4 py-3.5 rounded-xl bg-white/5 border border-gray-700/50 text-white placeholder-gray-500 text-sm focus:outline-none focus:border-purple-500/50"
              required
            />
            <div className="flex gap-2">
              {['CLIENT', 'PROVIDER'].map(t => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setForm(f => ({ ...f, type: t }))}
                  className={`flex-1 py-3 rounded-xl text-sm font-medium transition-all ${
                    form.type === t
                      ? 'bg-purple-600/20 border border-purple-500/50 text-purple-300'
                      : 'bg-white/5 border border-gray-700/50 text-gray-400'
                  }`}
                >
                  {t === 'CLIENT' ? '🚀 I need services' : '💼 I offer services'}
                </button>
              ))}
            </div>
            <button
              type="submit"
              disabled={submitting}
              className="w-full py-4 rounded-xl font-semibold text-white text-base mt-1 transition-all disabled:opacity-50"
              style={{ background: 'linear-gradient(135deg, #6C5CE7 0%, #a855f7 100%)' }}
            >
              {submitting ? 'Joining...' : 'Join the Waitlist'}
            </button>
            {status === 'error' && (
              <p className="text-red-400 text-sm text-center">Something went wrong. Try again.</p>
            )}
          </form>
        )}

        {status !== 'success' && (
          <p className="text-gray-500 text-xs mt-4">117 people already joined</p>
        )}

        {/* Access code link */}
        <button
          onClick={() => {
            const code = prompt('Enter access code:');
            if (code && code === 'FINDR2026') {
              localStorage.setItem('findr_access', 'granted');
              window.location.href = '/';
            } else if (code) {
              alert('Invalid access code');
            }
          }}
          className="text-gray-600 text-xs mt-8 hover:text-gray-400 transition-colors"
        >
          Have an access code?
        </button>
      </div>

      {/* Bottom */}
      <div className="relative z-10 mt-auto pt-6 text-center" style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 8px) + 12px)' }}>
        <p className="text-gray-600 text-xs">
          Available in Kuwait · UAE · KSA · Qatar · Bahrain · Oman
        </p>
      </div>
    </div>
  );
}
